import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { SectionTitle } from '../../shared/components';
import { Table } from '../../shared/components/data-display';
import useAsyncData from '../../shared/hooks/useAsyncData';
import { uploadFile } from '../../shared/service';
import {
  AccountsJob,
  createDebit,
  DebitRecord,
  DebitType,
  getAccountsJobs,
  getDebits,
  updateSalaryDebit,
} from '../accounts.api';

const money = (value: unknown) =>
  value == null || value === ''
    ? '—'
    : `₹ ${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const TABS: Array<{ label: string; type: 'credits' | DebitType }> = [
  { label: 'Credits', type: 'credits' },
  { label: 'Salary', type: 'salary' },
  { label: 'Billings', type: 'billings' },
  { label: 'Reimbursements', type: 'reimbursements' },
];

const creditColumns = [
  { id: 'job_code', dataKey: 'job_code', label: 'Job Code', align: 'left' as const, sortable: true },
  { id: 'job_name', dataKey: 'job_name', label: 'Project', align: 'left' as const, sortable: true },
  { id: 'client_name', dataKey: 'client_name', label: 'Client', align: 'left' as const },
  {
    id: 'job_value',
    dataKey: 'job_value',
    label: 'Job Value',
    align: 'right' as const,
    sortable: true,
    renderCell: money,
  },
  {
    id: 'amount_requested',
    dataKey: 'amount_requested',
    label: 'Requested',
    align: 'right' as const,
    renderCell: money,
  },
  {
    id: 'amount_credited',
    dataKey: 'amount_credited',
    label: 'Credited',
    align: 'right' as const,
    renderCell: money,
  },
  {
    id: 'balance',
    dataKey: 'balance',
    label: 'Balance',
    align: 'right' as const,
    renderCell: money,
  },
];

const statusChip = (value: string) => {
  if (!value) return <span className='unavailable-message'>—</span>;
  const paid = String(value).toUpperCase() === 'PAID';
  return <Chip size='small' label={value} color={paid ? 'success' : 'default'} />;
};

const debitColumns = [
  { id: 'id', dataKey: 'id', label: 'ID', align: 'center' as const, sortable: true },
  {
    id: 'employee_name',
    dataKey: 'employee_name',
    label: 'Employee',
    align: 'left' as const,
    sortable: true,
  },
  { id: 'particulars', dataKey: 'particulars', label: 'Particulars', align: 'left' as const },
  {
    id: 'amount',
    dataKey: 'amount',
    label: 'Amount',
    align: 'right' as const,
    sortable: true,
    renderCell: money,
  },
  { id: 'payment_method', dataKey: 'payment_method', label: 'Method', align: 'left' as const },
  {
    id: 'payment_date',
    dataKey: 'payment_date',
    label: 'Date',
    align: 'center' as const,
    sortable: true,
  },
  {
    id: 'status',
    dataKey: 'status',
    label: 'Status',
    align: 'center' as const,
    renderCell: statusChip,
  },
];

const fileLink = (value: unknown) =>
  value ? (
    <a href={String(value)} target='_blank' rel='noreferrer'>
      View
    </a>
  ) : (
    <span className='unavailable-message'>—</span>
  );

/**
 * Salary debits are one row per MONTH for the whole company, not one per employee — they carry
 * a period, a headcount and a total, and none of the employee/particulars/method keys the
 * generic debit columns read. Rendered with those columns, every cell showed "—".
 */
const salaryColumns = (onMarkPaid: (row: DebitRecord) => void) => [
  { id: 'period', dataKey: 'period', label: 'Month', align: 'left' as const, sortable: true },
  {
    id: 'employee_count',
    dataKey: 'employee_count',
    label: 'Employees',
    align: 'center' as const,
    sortable: true,
  },
  {
    id: 'total_amount',
    dataKey: 'total_amount',
    label: 'Total',
    align: 'right' as const,
    sortable: true,
    renderCell: money,
  },
  {
    id: 'attendance_sheet',
    dataKey: 'attendance_sheet',
    label: 'Sheet',
    align: 'center' as const,
    renderCell: fileLink,
  },
  {
    id: 'payment_proof',
    dataKey: 'payment_proof',
    label: 'Proof',
    align: 'center' as const,
    renderCell: fileLink,
  },
  {
    id: 'status',
    dataKey: 'status',
    label: 'Status',
    align: 'center' as const,
    renderCell: statusChip,
  },
  {
    id: 'actions',
    dataKey: 'id',
    label: '',
    align: 'center' as const,
    renderCell: (_value: unknown, row: DebitRecord) =>
      String(row.status).toUpperCase() === 'PAID' ? (
        <span className='unavailable-message'>—</span>
      ) : (
        <Button size='small' variant='outlined' onClick={() => onMarkPaid(row)}>
          Mark paid
        </Button>
      ),
  },
];

const emptyDebit = {
  debit_type: 'billings' as DebitType,
  amount: '',
  particulars: '',
  payment_method: '',
  payment_date: '',
};

/** Credits (money in, per job) and debits (salary / billings / reimbursements). */
export default function PaymentsPage() {
  const [tab, setTab] = useState(0);
  const active = TABS[tab];

  const fetcher = useCallback(
    () => (active.type === 'credits' ? getAccountsJobs() : getDebits(active.type)),
    [active.type]
  );

  const { data, loading, error, refetch } = useAsyncData<AccountsJob[] | DebitRecord[]>(
    fetcher,
    [],
    {
      code: 'PAYMENTS_FETCH_FAILED',
      errorMessage: `Failed to load ${active.label.toLowerCase()}`,
    }
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyDebit);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [payingRow, setPayingRow] = useState<DebitRecord | null>(null);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  const columns = useMemo(() => {
    if (active.type === 'credits') return creditColumns;
    if (active.type === 'salary') return salaryColumns((row) => {
      setProofFile(null);
      setPayError(null);
      setPayingRow(row);
    });
    return debitColumns;
  }, [active.type]);

  const setField = (name: string, value: string) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  const openCreate = () => {
    setForm({
      ...emptyDebit,
      debit_type: active.type === 'credits' ? 'billings' : (active.type as DebitType),
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!form.amount) {
      setFormError('Amount is required.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      await createDebit({
        debit_type: form.debit_type,
        amount: form.amount,
        particulars: form.particulars || undefined,
        payment_method: form.payment_method || undefined,
        payment_date: form.payment_date || undefined,
      });
      toast.success('Debit entry created');
      setDialogOpen(false);
      if (active.type === form.debit_type) await refetch();
    } catch (err) {
      setFormError(
        logError({ message: 'Failed to create debit', error: err, code: 'DEBIT_CREATE_FAILED' })
      );
    } finally {
      setSaving(false);
    }
  };

  /** Settles a month. Once paid, HR can no longer change that month's attendance or payslips. */
  const handleMarkPaid = async () => {
    if (!payingRow?.id) return;
    setSaving(true);
    setPayError(null);
    try {
      const proofId = proofFile ? (await uploadFile(proofFile)).id : undefined;
      await updateSalaryDebit(payingRow.id, {
        status: 'paid',
        ...(proofId ? { payment_proof: Number(proofId) } : {}),
      });
      toast.success('Salary month marked as paid');
      setPayingRow(null);
      await refetch();
    } catch (err) {
      setPayError(
        logError({ message: 'Failed to mark the month paid', error: err, code: 'SALARY_PAY_FAILED' })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={3}>
      <SectionTitle
        title='PAYMENTS'
        action={
          <Button
            variant='contained'
            startIcon={<Icon icon='mdi:plus' />}
            onClick={openCreate}
          >
            New Debit
          </Button>
        }
      />

      <Tabs value={tab} onChange={(_e, value: number) => setTab(value)} variant='scrollable'>
        {TABS.map((item) => (
          <Tab key={item.type} label={item.label} />
        ))}
      </Tabs>

      {error && (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Table
        key={active.type}
        columns={columns}
        data={data}
        loading={loading}
        emptyMessage={`No ${active.label.toLowerCase()} found`}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>New Debit Entry</DialogTitle>
        <DialogContent>
          <Stack gap={2.5} sx={{ paddingTop: 1 }}>
            {formError && <Alert severity='error'>{formError}</Alert>}

            <TextField
              select
              label='Debit Type'
              value={form.debit_type}
              onChange={(e) => setField('debit_type', e.target.value)}
              fullWidth
            >
              <MenuItem value='salary'>Salary</MenuItem>
              <MenuItem value='billings'>Billings</MenuItem>
              <MenuItem value='reimbursements'>Reimbursements</MenuItem>
            </TextField>

            <TextField
              label='Amount'
              type='number'
              required
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              fullWidth
            />

            <TextField
              label='Particulars'
              multiline
              minRows={2}
              value={form.particulars}
              onChange={(e) => setField('particulars', e.target.value)}
              fullWidth
            />

            <TextField
              label='Payment Method'
              value={form.payment_method}
              onChange={(e) => setField('payment_method', e.target.value)}
              helperText='e.g. Bank Transfer, Cash, UPI'
              fullWidth
            />

            <TextField
              label='Payment Date'
              type='date'
              value={form.payment_date}
              onChange={(e) => setField('payment_date', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleCreate} disabled={saving}>
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(payingRow)} onClose={() => setPayingRow(null)} fullWidth maxWidth='xs'>
        <DialogTitle>Mark {String(payingRow?.period ?? 'month')} as paid</DialogTitle>
        <DialogContent>
          <Stack gap={2.5} sx={{ paddingTop: 1 }}>
            {payError && <Alert severity='error'>{payError}</Alert>}
            <Alert severity='warning'>
              {money(payingRow?.total_amount)} for {String(payingRow?.employee_count ?? 0)}{' '}
              employee(s). This closes the month — HR can no longer change its attendance, and the
              payslips become the record of what was paid.
            </Alert>
            <Button component='label' variant='outlined'>
              {proofFile ? proofFile.name : 'Attach payment proof (optional)'}
              <input
                hidden
                type='file'
                onChange={(e) => setProofFile(e.target.files?.[0] ?? null)}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={() => setPayingRow(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleMarkPaid} disabled={saving}>
            {saving ? 'Saving…' : 'Mark paid'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
